import styled from '@emotion/styled'
import { ApiContentSection, ApiSectionType } from '@storefronts/common-types'
import React, { useCallback, useEffect, useState } from 'react'
import {
    SECONDARY_FONT_CSS_VAR,
    PRIMARY_PAGE_TEXT_COLOR_VAR,
} from '../../../context/CustomizationContextProvider'
import { Constants } from '../../../style/styleConstants'
import { getTypographyRoleStyle } from '../../../style/typography'
import { TYPOGRAPHY_SIZE } from '../../../style/typographySizes'
import { SectionContainer, SectionTitle } from './Shared'
import { ChevronDownIcon, ChevronUpIcon } from '../../icons/Chevron'
import { scaledFontSize, ScrollingLayout } from '../../../style/scrollingLayout'
import { hoverFadeText } from '../../../style/interactiveStates'

type AccordionSection = Extract<
    ApiContentSection,
    { type: ApiSectionType.ACCORDION }
>

const AccordionItemWrapper = styled.div({
    borderTop: `1px solid color-mix(in srgb, var(${PRIMARY_PAGE_TEXT_COLOR_VAR}) 25%, transparent)`,
    '&:first-child': {
        borderTop: 'none',
    },
})

const AccordionItemTitle = styled.h2<{
    textColor?: string
    isFirst?: boolean
    isLast?: boolean
}>(({ textColor, isFirst, isLast }) => ({
    margin: 0,
    padding: `${isFirst ? Constants.SECTION_BASE_SPACING_PX : 10}px 32px ${
        isLast ? Constants.SECTION_BASE_SPACING_PX : 10
    }px 0`,
    ...getTypographyRoleStyle('featureTitle', {
        fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
        fontWeight: 600,
        lineHeight: '1.4',
    }),
    fontSize: scaledFontSize(TYPOGRAPHY_SIZE.LABEL),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    color: textColor || `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
    position: 'relative',
    ...hoverFadeText(),
    '& svg': {
        position: 'absolute',
        right: 0,
        top: '50%',
        transform: 'translateY(-50%)',
    },
}))

const accordionContentHeadingRole = (fontWeight: number, lineHeight: number) =>
    getTypographyRoleStyle('featureTitle', {
        fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
        fontWeight,
        lineHeight,
    })

const AccordionItemContent = styled.div<{ textColor?: string }>(
    ({ textColor }) => {
        const resolvedColor = textColor || `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`
        return {
            padding: '0 0 12px',
            ...getTypographyRoleStyle('bodyText', {
                fontWeight: 400,
                lineHeight: '1.3',
            }),
            fontSize: scaledFontSize(TYPOGRAPHY_SIZE.ACCORDION_BODY),
            color: resolvedColor,
            '& *': {
                color: resolvedColor,
            },
            '& h1': {
                ...accordionContentHeadingRole(700, 1.35),
                fontSize: scaledFontSize(TYPOGRAPHY_SIZE.ACCORDION_CONTENT_H1),
                margin: 0,
            },
            '& h2': {
                ...accordionContentHeadingRole(600, 1.35),
                fontSize: scaledFontSize(TYPOGRAPHY_SIZE.ACCORDION_CONTENT_H2),
                margin: 0,
            },
            '& h3, & h4, & h5, & h6': {
                ...accordionContentHeadingRole(600, 1.3),
                fontSize: scaledFontSize(TYPOGRAPHY_SIZE.ACCORDION_CONTENT_H3),
                margin: 0,
            },
            '& p': {
                margin: '0.5em 0',
            },
            '& blockquote': {
                borderLeft: '4px solid rgb(209 213 219)',
                paddingLeft: '1rem',
                paddingTop: '0.5rem',
                paddingBottom: '0.5rem',
                fontStyle: 'italic',
                margin: '0.5em 0',
            },
            '& hr': {
                border: 'none',
                borderTop: '1px solid rgb(209 213 219)',
                marginTop: '1rem',
                marginBottom: '1rem',
            },
            '& ul': {
                listStyleType: 'disc',
                listStylePosition: 'outside',
                paddingLeft: '20px',
                margin: '8px 0',
            },
            '& ol': {
                listStyleType: 'decimal',
                listStylePosition: 'outside',
                paddingLeft: '20px',
                margin: '8px 0',
            },
            '& li': {
                marginBottom: '4px',
                paddingLeft: '4px',
            },
            '& ::marker': {
                color: 'inherit',
            },
        }
    },
)

const AccordionItem: React.FC<{
    title: string
    content: string | HTMLElement
    isOpen: boolean
    toggleOpen: () => void
    textColor?: string
    isFirst?: boolean
    isLast?: boolean
}> = ({ title, content, isOpen, toggleOpen, textColor, isFirst, isLast }) => {
    const borderStyle = textColor
        ? {
              borderTopColor: isFirst
                  ? 'transparent'
                  : `color-mix(in srgb, ${textColor} 25%, transparent)`,
          }
        : undefined

    return (
        <AccordionItemWrapper style={borderStyle}>
            <AccordionItemTitle
                onClick={toggleOpen}
                textColor={textColor}
                isFirst={isFirst}
                isLast={isLast}
            >
                {title}
                {isOpen ? (
                    <ChevronUpIcon stroke="currentColor" />
                ) : (
                    <ChevronDownIcon stroke="currentColor" />
                )}
            </AccordionItemTitle>
            {isOpen && (
                <AccordionItemContent
                    textColor={textColor}
                    dangerouslySetInnerHTML={{ __html: content }}
                />
            )}
        </AccordionItemWrapper>
    )
}
AccordionItem.displayName = 'AccordionItem'

interface AccordionProps {
    section: AccordionSection
}

export const Accordion = React.memo(function Accordion({
    section,
}: AccordionProps) {
    const [openItems, setOpenItems] = useState<number[]>([])

    useEffect(() => {
        const initialOpenItems = (section.items ?? []).reduce<number[]>(
            (acc, item, index) => {
                return item.autoExpand ? [...acc, index] : acc
            },
            [],
        )
        setOpenItems(initialOpenItems)
    }, [section.items])

    const toggleItem = useCallback((index: number) => {
        setOpenItems((prev) =>
            prev.includes(index)
                ? prev.filter((i) => i !== index)
                : [...prev, index],
        )
    }, [])

    const textColor = section.textColor

    return (
        <SectionContainer maxContentWidth={ScrollingLayout.PROSE_MAX_WIDTH}>
            {section.sectionTitle && (
                <SectionTitle
                    style={{
                        marginBottom: 0,
                        ...(textColor ? { color: textColor } : undefined),
                    }}
                >
                    {section.sectionTitle}
                </SectionTitle>
            )}
            <div>
                {(section.items ?? []).map((item, idx, arr) => (
                    <AccordionItem
                        key={idx}
                        title={item.title}
                        content={item.content}
                        isOpen={openItems.includes(idx)}
                        toggleOpen={() => toggleItem(idx)}
                        textColor={textColor}
                        isFirst={idx === 0}
                        isLast={idx === arr.length - 1}
                    />
                ))}
            </div>
        </SectionContainer>
    )
})
